import React from "react";
import { useTranslation } from "react-i18next";
import { GraphUser, AppSettings } from "../../types";

interface ApproversSectionProps {
  settings: AppSettings | null;
  selectedApprovers: GraphUser[];
  saving: boolean;
  onChangeApprovalMode: (mode: "basic" | "basicAwaitAll") => void;
  onOpenPeoplePicker: () => void;
  onRemoveApprover: (userId: string) => void;
}

export const ApproversSection: React.FC<ApproversSectionProps> = ({
  settings,
  selectedApprovers,
  saving,
  onChangeApprovalMode,
  onOpenPeoplePicker,
  onRemoveApprover,
}) => {
  const { t } = useTranslation('adminPage');

  return (
    <div className="setup-section">
      <h2>{t('sections.approvers.title')}</h2>
      <p className="help-text">{t('sections.approvers.description')}</p>
      
      <div className="warning-card admin-mb-16">
        <div className="admin-info-note-text">
          {t('sections.approvers.note')}
        </div>
      </div>

      {/* Paramètre de mode d'approbation */}
      <div className="status-card admin-mb-16">
        <div className="status-content">
          <label className="admin-approval-mode-label">
            {t('sections.approvalMode.title')}
          </label>

          <div className="admin-approval-warning-box">
            <div className="admin-approval-warning-text">
              {t('sections.approvalMode.warning')}
            </div>
          </div>

          <div className={`admin-approval-options-container${saving ? ' saving' : ''}`}>
            {/* Option 1: Une seule approbation suffit */}
            <label className={`admin-radio-option${settings?.notificationSettings?.approvalMode !== "basicAwaitAll" ? ' selected' : ''}`}>
              <input
                type="radio"
                name="approvalMode"
                value="basic"
                checked={settings?.notificationSettings?.approvalMode !== "basicAwaitAll"}
                onChange={() => onChangeApprovalMode("basic")}
                disabled={saving}
                className="admin-radio-input"
              />
              <div className="admin-radio-content">
                <div className="admin-radio-title">
                  {t('sections.approvalMode.basic.title')}
                </div>
                <div className="admin-radio-desc">
                  {t('sections.approvalMode.basic.description')}
                </div>
              </div>
            </label>

            {/* Option 2: Tous les approbateurs doivent répondre */}
            <label className={`admin-radio-option${settings?.notificationSettings?.approvalMode === "basicAwaitAll" ? ' selected' : ''}`}>
              <input
                type="radio"
                name="approvalMode"
                value="basicAwaitAll"
                checked={settings?.notificationSettings?.approvalMode === "basicAwaitAll"}
                onChange={() => onChangeApprovalMode("basicAwaitAll")}
                disabled={saving}
                className="admin-radio-input"
              />
              <div className="admin-radio-content">
                <div className="admin-radio-title">
                  {t('sections.approvalMode.basicAwaitAll.title')}
                </div>
                <div className="admin-radio-desc">
                  {t('sections.approvalMode.basicAwaitAll.description')}
                </div>
              </div>
            </label>
          </div>

          {saving && (
            <div className="admin-saving-msg">
              <div className="admin-spinner-inline" />
              <span>{t('messages.saving')}</span>
            </div>
          )}
        </div>
      </div>

      <div className="status-card">
        <div className="status-content">
          <div className="admin-status-header">
            <h3 className="admin-section-title">
              {t('sections.approvers.count', { count: selectedApprovers.length })}
            </h3>
            <button
              onClick={onOpenPeoplePicker}
              disabled={saving}
              className="admin-add-btn"
            >
              {t('sections.approvers.add')}
            </button>
          </div>

          {selectedApprovers.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('sections.approvers.tableHeaders.name')}</th>
                    <th>{t('sections.approvers.tableHeaders.email')}</th>
                    <th>{t('sections.approvers.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedApprovers.map((approver) => {
                    const isLastApprover = selectedApprovers.length <= 1;
                    const isDisabled = saving || isLastApprover;

                    return (
                      <tr key={approver.id}>
                        <td>
                          <div className="table-name">{approver.displayName}</div>
                        </td>
                        <td>
                          <div className="table-label">{approver.mail || approver.userPrincipalName}</div>
                        </td>
                        <td>
                          <button
                            onClick={() => onRemoveApprover(approver.id)}
                            disabled={isDisabled}
                            title={
                              isLastApprover
                                ? t('sections.approvers.removeTooltip.lastApprover')
                                : t('sections.approvers.removeTooltip.default')
                            }
                            className="admin-remove-btn"
                          >
                            {t('sections.approvers.remove')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="errors-section">
              <p>⚠️ {t('sections.approvers.noApprovers')}</p>
              <p className="admin-mt-8 admin-text-small">
                {t('sections.approvers.noApproversHint')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
